import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { Plus, Check, X, Clock, AppWindow, ShieldQuestion } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function DynIcon({ name, className }) {
  const C = Icons[name] || AppWindow;
  return <C className={className} />;
}

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  denied: "bg-red-100 text-red-700",
};

export default function Requests() {
  const { has } = useAuth();
  const canReview = has("apps.edit");
  const [requests, setRequests] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [appId, setAppId] = useState("");
  const [reason, setReason] = useState("");

  const load = () => {
    api.get("/access-requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/apps/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const requestable = catalog.filter((a) => !a.has_access && !a.pending);

  const submit = async () => {
    if (!appId) { toast.error("Select an app"); return; }
    try {
      await api.post("/access-requests", { app_id: appId, reason });
      toast.success("Request submitted");
      setOpen(false); setAppId(""); setReason("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const decide = async (rid, action) => {
    try {
      await api.post(`/access-requests/${rid}/${action}`, { note: "" });
      toast.success(action === "approve" ? "Request approved" : "Request denied");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const pending = requests.filter((r) => r.status === "pending");
  const decided = requests.filter((r) => r.status !== "pending");

  return (
    <div data-testid="requests-page">
      <PageHeader testid="requests-header" title="Access Requests" subtitle="Request access to apps and review pending approvals.">
        <Button data-testid="new-request-button" onClick={() => setOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus className="h-4 w-4" /> Request access
        </Button>
      </PageHeader>

      <Card className="border-slate-200 shadow-sm p-5">
        <div className="mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-500" />
          <h3 className="font-heading text-sm font-semibold text-slate-800">Pending {canReview ? "approvals" : "requests"}</h3>
          <Badge variant="secondary" className="ml-1">{pending.length}</Badge>
        </div>
        {pending.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400" data-testid="requests-empty">No pending requests.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {pending.map((r) => (
              <div key={r.request_id} className="flex items-center justify-between gap-3 py-3" data-testid={`request-${r.request_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800">{r.app_name}</div>
                  <div className="text-xs text-slate-400">{r.requester_name} · {r.requester_email}{r.reason ? ` — "${r.reason}"` : ""}</div>
                </div>
                {canReview ? (
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" variant="outline" data-testid={`approve-${r.request_id}`} onClick={() => decide(r.request_id, "approve")} className="border-emerald-200 text-emerald-600 hover:bg-emerald-50">
                      <Check className="h-4 w-4" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`deny-${r.request_id}`} onClick={() => decide(r.request_id, "deny")} className="border-red-200 text-red-600 hover:bg-red-50">
                      <X className="h-4 w-4" /> Deny
                    </Button>
                  </div>
                ) : (
                  <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${STATUS_BADGE.pending}`}>Pending</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="mt-4 border-slate-200 shadow-sm p-5">
        <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">History</h3>
        {decided.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-400">No past requests.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {decided.map((r) => (
              <div key={r.request_id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`request-${r.request_id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-slate-700">{r.app_name}</div>
                  <div className="text-xs text-slate-400">{r.requester_name}{r.reviewer_name ? ` · by ${r.reviewer_name}` : ""}</div>
                </div>
                <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium capitalize ${STATUS_BADGE[r.status]}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldQuestion className="h-5 w-5 text-primary" /> Request app access</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {requestable.length === 0 ? (
              <div className="rounded-md bg-slate-50 px-3 py-4 text-center text-sm text-slate-400">No apps available to request — you already have access or have pending requests.</div>
            ) : (
              <>
                <Select value={appId} onValueChange={setAppId}>
                  <SelectTrigger data-testid="request-app-select"><SelectValue placeholder="Select an app" /></SelectTrigger>
                  <SelectContent>
                    {requestable.map((a) => (
                      <SelectItem key={a.app_id} value={a.app_id}>
                        <span className="flex items-center gap-2"><DynIcon name={a.icon} className="h-4 w-4 text-slate-400" /> {a.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea data-testid="request-reason-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why do you need access? (optional)" />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="submit-request-button" disabled={!appId} onClick={submit} className="bg-primary hover:bg-primary/90 text-primary-foreground">Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
